import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import map from 'lodash/map';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import selectPpId from 'yoda-site-components/lib/selectors/productDetails/selectPpId';
import OfferingsActions from 'yoda-checkout-components/lib/actions/OfferingsAction';
import ItemCountAction from 'yoda-site-components/lib/actions/ItemCountAction';
import { selectedProtectionPlanWrapper } from 'yoda-site-components/lib/selectors/PdpNameSpaceSelector';
import { ProtectionPlanOptions } from 'yoda-site-components/lib/components/ProtectionPlan/ProtectionPlan.config';
import LoaderAction from 'yoda-site-components/lib/actions/LoadingAction';
import { selectMarketPlaceSellerInfo } from 'yoda-site-components/lib/selectors/MarketPlaceSelector';
import { selectFeatureFlags } from 'yoda-site-components/lib/selectors/ContextSelector';
import deliveryOptions from '../../selectors/delivery/deliverOptionsSelector';
import selectSkuId from '../../selectors/selectedByUser/selectSkuId';
import selectAvailableForZipcode from '../../selectors/delivery/selectAvailableForZipcode';
import { addItemToCart, updateCartItem } from '../../actions/AddToCartAction';

/**
 * Higher order component of add item to cart.
 * @param {String} wrappedComponentParam  Property of Wrapped component to perform wrapped component changes.
 */
const withAddToCartHOC = (wrappedComponentParam = {}) => { // eslint-disable-line
    /**
     * @param {Object} WrappedComponent     React Component
     */

    return function Factory(WrappedComponent) {
        class WithAddToCartHoc extends Component { // eslint-disable-line

            static propTypes = {
                customOptions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
                ppId: PropTypes.string,
                selectedQuantity: PropTypes.number,
                query: PropTypes.oneOfType([PropTypes.object]),
                actions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
                deliveryOption: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
                deliveryZipcode: PropTypes.string,
                availableForZipcode: PropTypes.bool,
                selectedStore: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
                selectedProtectionPlan: PropTypes.string,
                productOffering: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
                marketPlaceSellerInfo: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
                featureFlags: PropTypes.oneOfType([PropTypes.object]),
            };

            static defaultProps = {
                selectedQuantity: 1,
                customOptions: [],
                ppId: '',
                query: {},
                actions: {},
                deliveryOption: {},
                deliveryZipcode: '',
                availableForZipcode: false,
                selectedStore: {},
                selectedProtectionPlan: '',
                productOffering: {},
                marketPlaceSellerInfo: {},
                featureFlags: {},
            };

            /* istanbul ignore next */
            constructor() {
                super();
                this.addProductToCart = this.addProductToCart.bind(this);
                this.getDeliveryOptionDetails = this.getDeliveryOptionDetails.bind(this);
                this.getChildItems = this.getChildItems.bind(this);
                this.updateCartDetails = this.updateCartDetails.bind(this);
                this.cartDetails = {};
            }

            /* istanbul ignore next */
            getDeliveryOptionDetails(sdpuStoreId = '') {
                const {
                    deliveryOption,
                    deliveryZipcode,
                    availableForZipcode,
                    selectedStore,
                } = this.props;
                const { isStoreModalAtc } = wrappedComponentParam;

                const deliveryOptionObj = {
                    type: 'HOME',
                    selected: true,
                    label:
                        deliveryOption &&
                        Object.keys(deliveryOption).length &&
                        deliveryOption.shipToHomeOrStore
                            ? deliveryOption.shipToHomeOrStore.label
                            : null,
                };

                /* istanbul ignore if */
                if (deliveryZipcode && availableForZipcode) {
                    deliveryOptionObj.zipcode = deliveryZipcode;
                }

                const isBopisChecked =
                    isStoreModalAtc ||
                    (deliveryOption.sameDayPickUp && deliveryOption.sameDayPickUp.isChecked);

                if (isBopisChecked) {
                    const defaultStore = LocalStorage.getData('defaultStore', true);
                    const storeFromLocal = defaultStore ? JSON.parse(defaultStore) : {};

                    deliveryOptionObj.type = 'BOPIS';
                    deliveryOptionObj.storeId =
                        sdpuStoreId || selectedStore.id || storeFromLocal.id || '';
                }
                return deliveryOptionObj;
            }

            /* istanbul ignore next */
            getChildItems() {
                const { ppId, selectedProtectionPlan } = this.props;
                const childItems = [];
                if (!isEmpty(selectedProtectionPlan) && selectedProtectionPlan !== 'no') {
                    childItems.push({
                        skuId: selectedProtectionPlan,
                        ppId,
                        quantity: 1,
                    });
                    /* istanbul ignore if */
                    if (selectedProtectionPlan === ProtectionPlanOptions.no) {
                        this.props.actions.setCarePlanUnSelected();
                    } else {
                        this.props.actions.setOfferingSelected({ itemId: selectedProtectionPlan });
                    }
                    // this.triggerCarePlanAcceptAnalytics(selectedProtectionPlan, ppId);
                }
                return childItems;
            }

            /* istanbul ignore next */
            updateCartDetails = (details) => {
                Object.keys(details).forEach((key) => {
                    this.cartDetails[key] = details[key];
                });
            };

            /* istanbul ignore next */
            addProductToCart = (
                skuIds,
                sdpuStoreInfo = {},
                isChangeStoreAtc = false,
                updateStoreIdsInQuery = {}
            ) => {
                const {
                    ppId,
                    selectedQuantity,
                    customOptions: { options, selectedCustomOptions },
                    query,
                    actions,
                    deliveryOption,
                    productOffering,
                    marketPlaceSellerInfo,
                    featureFlags,
                } = this.props;
                const { isStoreModalAtc } = wrappedComponentParam;
                const { enableMarketPlace = false } = featureFlags || {};
                const { pricing = {} } = productOffering || {};
                const { offerId } = pricing || {};
                const isMarketplaceProduct =
                    enableMarketPlace && marketPlaceSellerInfo?.isMarketPlaceProduct;

                const addToCartData = map(skuIds, (skuId) => {
                    const cartItem = {
                        skuId,
                        ppId,
                        quantity: selectedQuantity || 1,
                        deliveryOption: this.getDeliveryOptionDetails(sdpuStoreInfo.storeId),
                        childItems: this.getChildItems(),
                    };
                    if (enableMarketPlace && isMarketplaceProduct) {
                        cartItem.offer = {
                            offerId,
                            itemSourceType: 'MKL',
                        };
                    }

                    return cartItem;
                });

                const customAttributes = [];
                const redirectedFromSFL = query.source && query.source === 'sfl';

                /* istanbul ignore if */
                if (options && addToCartData.length) {
                    options.fields.forEach((option) => {
                        const selectedOption = selectedCustomOptions.find(
                            (customOption) => customOption.type === option.name
                        );
                        selectedOption &&
                            customAttributes.push({
                                type: selectedOption.type,
                                value: selectedOption.value,
                                isVdata: true,
                            });
                    });
                    addToCartData[0].attributes = customAttributes;
                }

                this.updateCartDetails(addToCartData[0]);

                const atcparamsData = {
                    interstitial: true,
                    isStoreModalAtc,
                    sdpuStoreInfo,
                    deliveryOption,
                    isChangeStoreAtc,
                };
                if (query.cartId) {
                    actions.showLoader();
                    actions.updateCartItem({
                        ...atcparamsData,
                        id: query.cartId,
                        params: addToCartData[0],
                    });
                    actions.updateItemCountAction();
                } else {
                    const atcParams = {
                        ...atcparamsData,
                        ...updateStoreIdsInQuery,
                        data: addToCartData,
                    };
                    if (redirectedFromSFL && includes(skuIds, query.skuId)) {
                        atcParams.source = 'sfl';
                    }
                    this.props.actions.addItemToCart(atcParams);
                }
            };

            render() {
                const newProps = {
                    getChildItems: this.getChildItems,
                    getDeliveryOptionDetails: this.getDeliveryOptionDetails,
                    addProductToCart: this.addProductToCart,
                    cartDetails: this.cartDetails,
                };
                const props = { ...this.props, ...newProps };
                return <WrappedComponent {...props} />;
            }
        }

        const mapStateToProps = (state) => ({
            ppId: selectPpId(state),
            selectedQuantity: parseInt(state.selectedQuantity, 10),
            customOptions: state.customOptions,
            skuId: selectSkuId(state),
            deliveryZipcode: state.deliveryZipcode,
            deliveryOption:
                Object.keys(deliveryOptions(state).shipToHomeOrStore).length > 0
                    ? deliveryOptions(state)
                    : state.deliveryOption,
            selectedProtectionPlan: selectedProtectionPlanWrapper(false)(state),
            availableForZipcode: selectAvailableForZipcode(state),
            selectedStore:
                state.selectedStore && state.selectedStore.storeDetails
                    ? state.selectedStore.storeDetails
                    : {},
            productOffering: state.productOffering || {},
            marketPlaceSellerInfo: selectMarketPlaceSellerInfo(false)(state),
            featureFlags: selectFeatureFlags(state),
        });

        const mapDispatchToProps = (dispatch) => ({
            actions: bindActionCreators(
                {
                    /* Add To Cart Actions */
                    addItemToCart,
                    updateCartItem,
                    ...LoaderAction,
                    ...ItemCountAction,
                    ...OfferingsActions,
                },
                dispatch
            ),
        });
        return connect(mapStateToProps, mapDispatchToProps)(WithAddToCartHoc);
    };
};

export default withAddToCartHOC;
